import * as React from 'react';
import { range, isInteger } from 'lodash-es';
import { DateTime, Duration } from 'luxon';
import { Trans } from '@lingui/react';
import { GqlWarrantProgram, GqlWarrantProgramKind } from '../../../../graphql/base-types.graphql';

export const DEFAULT_DURATION: { [k in GqlWarrantProgramKind]: number } = {
  [GqlWarrantProgramKind.Employee]: 36,
  [GqlWarrantProgramKind.Warrant]: 48,
  [GqlWarrantProgramKind.Wise]: 48,
  [GqlWarrantProgramKind.Nice]: 24,
};

export const DURATION_RANGE: { [k in GqlWarrantProgramKind]: { min: number; max: number } } = {
  [GqlWarrantProgramKind.Employee]: { min: 36, max: 119 },
  [GqlWarrantProgramKind.Warrant]: { min: 1, max: 241 },
  [GqlWarrantProgramKind.Wise]: { min: 1, max: 241 },
  [GqlWarrantProgramKind.Nice]: { min: 1, max: 241 },
};

export type DurationArray = {
  value: number;
  text: React.JSX.Element;
}[];

export function createDurationArray(
  minMonths: number,
  maxMonths: number,
  onlyMonths = false,
  freqMonths = 0,
  startDate?: DateTime | null,
): DurationArray {
  const durationArray = range(minMonths, maxMonths).map(value => {
    const duration = Duration.fromObject({ months: value }, { conversionAccuracy: 'casual' });
    const years = Math.floor(duration.as('years'));
    const months = duration.minus({ years }).as('months');

    const endDateString = startDate?.plus({ months: value })?.toFormat('yyyy-MM-dd');

    let text: React.JSX.Element;
    if (onlyMonths) {
      text = <Trans id="{value} months" values={{ value }} />;
    } else {
      if (months === 0) {
        text = startDate ? (
          <Trans
            id="{value} months ({years} years. End: {endDate})"
            values={{ value, years, endDate: endDateString }}
          />
        ) : (
          <Trans id="{value} months ({years} years)" values={{ value, years }} />
        );
      } else {
        text = startDate ? (
          <Trans
            id="{value} months ({years} years and {months} months. End: {endDate})"
            values={{ value, years, months, endDate: endDateString }}
          />
        ) : (
          <Trans id="{value} months ({years} years and {months} months)" values={{ value, years, months }} />
        );
      }
    }
    return { value, text };
  });

  if (freqMonths > 0) {
    return durationArray.filter(da => isInteger(da.value / freqMonths));
  }
  return durationArray;
}

export const calculatedDefaultDuration = (
  warrantProgram: Pick<GqlWarrantProgram, 'warrantStartDate' | 'warrantEndDate' | 'warrantKind'>,
) => {
  if (warrantProgram?.warrantStartDate && warrantProgram?.warrantEndDate) {
    const startDate = DateTime.fromISO(warrantProgram.warrantStartDate);
    const endDate = DateTime.fromISO(warrantProgram.warrantEndDate);
    return Math.floor(endDate.diff(startDate, 'months').months);
  }
  return DEFAULT_DURATION[warrantProgram?.warrantKind ?? GqlWarrantProgramKind.Employee];
};
